import { useState } from 'react'
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Download,
  FileX2,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { PaginatedTable } from '@/components/common/PaginatedTable'
import {
  useProductListRejectedItems,
  useProductListWarningItems,
  useProductListErrorReport,
} from '@/hooks/useProductLists'
import { toast } from '@/stores/useToastStore'
import type { ProductListRead, ProductListItemRead } from '@/api/productLists'
import type { PlanItem } from '@/api/planItems'

interface ValidItemsPagination {
  items: PlanItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  isLoading: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

interface ImportResultsSummaryProps {
  productList: ProductListRead
  validPagination: ValidItemsPagination
}

const VALID_COLUMNS: {
  key: keyof PlanItem
  header: string
  width?: string
}[] = [
  { key: 'skuNumber', header: 'SKU #', width: '120px' },
  { key: 'description', header: 'Description' },
]

function renderErrors(_: unknown, item: ProductListItemRead) {
  if (!item.errors || item.errors.length === 0) return '—'
  return (
    <div className="space-y-1">
      {item.errors.map((err, i) => (
        <div key={i} className="text-sm">
          <span className="font-medium text-[#1C1C1C]">{err.column}</span>
          {err.value !== null && err.value !== '' && (
            <span className="text-[#556374]">
              {' '}
              = &quot;{String(err.value)}&quot;
            </span>
          )}
          <p className="text-[#556374] text-[13px]">{err.message}</p>
        </div>
      ))}
    </div>
  )
}

function makeIssueColumns(header: string) {
  return [
    { key: 'row_number' as const, header: 'Row', width: '60px' },
    { key: 'errors' as const, header, render: renderErrors },
  ]
}

const REJECTED_COLUMNS = makeIssueColumns('Issue')
const WARNING_COLUMNS = makeIssueColumns('Warning')

type IssueColumn = (typeof REJECTED_COLUMNS)[number]

export function ImportResultsSummary({
  productList,
  validPagination,
}: ImportResultsSummaryProps) {
  const [rejectedPage, setRejectedPage] = useState(1)
  const [rejectedPageSize, setRejectedPageSize] = useState(5)

  const hasRejected = productList.invalid_rows > 0

  const { data: rejectedData, isLoading: rejectedLoading } =
    useProductListRejectedItems(
      productList.id,
      rejectedPage,
      rejectedPageSize,
      hasRejected
    )

  const { data: warningItems, isLoading: warningLoading } =
    useProductListWarningItems(productList.id)

  const errorReportMutation = useProductListErrorReport()

  const hasWarnings = warningItems && warningItems.length > 0
  const hasIssues = hasRejected || hasWarnings

  const handleDownloadErrorReport = () => {
    errorReportMutation.mutate(productList.id, {
      onSuccess: data => {
        window.open(data.download_url, '_blank')
      },
      onError: (error: Error) => {
        toast.error(`Failed to download error report: ${error.message}`)
      },
    })
  }

  const defaultTab = hasRejected ? 'rejected' : 'warnings'

  const validItemsTable = (
    <div className="border rounded-lg overflow-hidden">
      <PaginatedTable<PlanItem>
        columns={VALID_COLUMNS}
        data={validPagination.items}
        page={validPagination.page}
        pageSize={validPagination.pageSize}
        totalCount={validPagination.totalCount}
        totalPages={validPagination.totalPages}
        pageSizeOptions={[5, 10, 25]}
        onPageChange={validPagination.onPageChange}
        onPageSizeChange={validPagination.onPageSizeChange}
        isLoading={validPagination.isLoading}
        getRowKey={item => item.id}
        emptyMessage="No items found in the uploaded file."
      />
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white p-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <p className="text-[18px] font-semibold text-[#1C1C1C]">
              {productList.valid_rows}
            </p>
            <p className="text-[12px] text-[#556374]">Rows imported</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white p-3">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <p className="text-[18px] font-semibold text-[#1C1C1C]">
              {productList.invalid_rows}
            </p>
            <p className="text-[12px] text-[#556374]">Rows rejected</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white p-3">
          <FileX2 className="h-5 w-5 text-[#556374] shrink-0" />
          <div>
            <p className="text-[18px] font-semibold text-[#1C1C1C]">
              {productList.skipped_rows}
            </p>
            <p className="text-[12px] text-[#556374]">Empty rows skipped</p>
          </div>
        </div>
      </div>

      {hasRejected && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {productList.invalid_rows} row
              {productList.invalid_rows !== 1 ? 's were' : ' was'} rejected due
              to validation errors.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-2 shrink-0 cursor-pointer"
              onClick={handleDownloadErrorReport}
              disabled={errorReportMutation.isPending}
            >
              {errorReportMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Download className="h-3 w-3 mr-1" />
              )}
              Download CSV
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {hasWarnings && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Some imported rows had warnings — certain fields were set to null or
            rounded.
          </AlertDescription>
        </Alert>
      )}

      {hasIssues ? (
        <Tabs defaultValue={defaultTab}>
          <TabsList className="w-full">
            <TabsTrigger value="valid" className="cursor-pointer">
              Valid Items ({validPagination.totalCount})
            </TabsTrigger>
            {hasRejected && (
              <TabsTrigger value="rejected" className="cursor-pointer">
                <AlertCircle className="h-3.5 w-3.5 mr-1 text-red-600" />
                Rejected ({productList.invalid_rows})
              </TabsTrigger>
            )}
            {hasWarnings && (
              <TabsTrigger value="warnings" className="cursor-pointer">
                <AlertTriangle className="h-3.5 w-3.5 mr-1 text-amber-600" />
                Warnings ({warningItems.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="valid" className="mt-2">
            {validItemsTable}
          </TabsContent>

          {hasRejected && (
            <TabsContent value="rejected" className="mt-2">
              <div className="border rounded-lg overflow-hidden">
                <PaginatedTable<ProductListItemRead>
                  columns={
                    REJECTED_COLUMNS as unknown as {
                      key: keyof ProductListItemRead
                      header: string
                      width?: string
                      render?: (
                        value: ProductListItemRead[keyof ProductListItemRead],
                        item: ProductListItemRead
                      ) => React.ReactNode
                    }[]
                  }
                  data={rejectedData?.data ?? []}
                  page={rejectedPage}
                  pageSize={rejectedPageSize}
                  totalCount={rejectedData?.pagination.total ?? 0}
                  totalPages={Math.ceil(
                    (rejectedData?.pagination.total ?? 0) / rejectedPageSize
                  )}
                  pageSizeOptions={[5, 10, 25]}
                  onPageChange={setRejectedPage}
                  onPageSizeChange={setRejectedPageSize}
                  isLoading={rejectedLoading}
                  getRowKey={item => item.id}
                  emptyMessage="No rejected rows."
                />
              </div>
            </TabsContent>
          )}

          {hasWarnings && (
            <TabsContent value="warnings" className="mt-2">
              <div className="border rounded-lg overflow-hidden">
                {warningLoading ? (
                  <div className="px-6 py-12 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-[#556374]" />
                    <span className="text-[14px] text-[#556374]">
                      Loading warnings...
                    </span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F8F8F8] border-b border-[#E5E7EB]">
                          {WARNING_COLUMNS.map((col: IssueColumn) => (
                            <th
                              key={col.key}
                              className="px-6 py-3 text-left text-[12px] font-semibold text-[#556374] uppercase tracking-wide"
                              style={
                                col.width ? { width: col.width } : undefined
                              }
                            >
                              {col.header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {warningItems.map((item, index) => (
                          <tr
                            key={item.id}
                            className={`border-b border-[#E5E7EB] last:border-b-0 ${index % 2 === 1 ? 'bg-[#FAFAFA]' : ''}`}
                          >
                            <td className="px-6 py-4 text-[14px] text-[#1C1C1C] font-medium">
                              {item.row_number}
                            </td>
                            <td className="px-6 py-4 text-[14px] text-[#556374]">
                              {renderErrors(null, item)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      ) : (
        validItemsTable
      )}
    </div>
  )
}
